"use server"

import { sdk } from "@lib/config"
import { getAuthHeaders, getCartId } from "./cookies"
import type { RentalInfo, RentalAvailability } from "@/types/rental"

/**
 * Fetch rental configuration for a product
 * Uses GET /store/products/:id/rental-info
 */
export async function getRentalInfo(
  productId: string,
  variantId?: string,
  currencyCode?: string
): Promise<RentalInfo | null> {
  try {
    const headers = await getAuthHeaders()
    const query: Record<string, string> = {}
    if (variantId) query.variantId = variantId
    if (currencyCode) query.currency_code = currencyCode

    const response = await sdk.client.fetch<RentalInfo>(
      `/store/products/${productId}/rental-info`,
      {
        method: "GET",
        query,
        headers,
        next: { revalidate: 300 },
      }
    )

    return response
  } catch (error) {
    console.error("Error fetching rental info:", error)
    return null
  }
}

/**
 * Check rental availability for date range
 * Uses GET /store/products/:id/rental-availability
 */
export async function checkRentalAvailability(
  productId: string,
  startDate: Date,
  endDate: Date,
  quantity: number = 1
): Promise<RentalAvailability | null> {
  try {
    const headers = await getAuthHeaders()

    const response = await sdk.client.fetch<RentalAvailability>(
      `/store/products/${productId}/rental-availability`,
      {
        method: "GET",
        query: {
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
          quantity: quantity.toString(),
        },
        headers,
        cache: "no-store",
      }
    )

    return response
  } catch (error) {
    console.error("Error checking rental availability:", error)
    return null
  }
}

/**
 * `YYYY-MM-DD` → полночь ЭТОГО дня в часовом поясе покупателя.
 *
 * `new Date("2026-11-06")` разбирается как полночь UTC, и западнее Гринвича
 * это уже пятое число: календарь погасил бы соседний день.
 */
function dayToLocalDate(day: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day)
  if (!match) return null
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
}

/**
 * Get booked dates for a product within a date range
 * Returns array of dates that are not available
 */
export async function getBookedDates(
  productId: string,
  startDate: Date,
  endDate: Date
): Promise<Date[]> {
  try {
    const availability = await checkRentalAvailability(productId, startDate, endDate, 1)

    if (!availability) {
      return []
    }

    // Сервер уже посчитал занятость по дням тем же правилом, что и
    // «сколько свободно». Читать вместо этого `inventory_breakdown` нельзя:
    // там строка — это inventory_item, то есть ВСЕ копии товара разом, и одна
    // бронь гасила весь календарь у товара, которому было что сдать.
    if (Array.isArray(availability.unavailable_dates)) {
      return availability.unavailable_dates.map(dayToLocalDate).filter(Boolean) as Date[]
    }

    // Запасной путь для бэка, который ещё не отдаёт `unavailable_dates`:
    // заведомо осторожный — лучше спрятать свободный день, чем продать занятый.
    const bookedDates: Date[] = []

    for (const item of availability.inventory_breakdown) {
      if (item.status === 'unavailable' && item.current_booking) {
        const bookingStart = new Date(item.current_booking.start_date)
        const bookingEnd = new Date(item.current_booking.end_date)

        // Add all dates in the booking range
        const current = new Date(bookingStart)
        while (current <= bookingEnd) {
          bookedDates.push(new Date(current))
          current.setDate(current.getDate() + 1)
        }
      }
    }

    return bookedDates
  } catch (error) {
    console.error("Error getting booked dates:", error)
    return []
  }
}
